import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faPen, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import Loading from "components/Loading";

import councilPortalService from "api/councilPortalService";
import {
  ECouncilorRole,
  ICouncilCouncilor,
  ISaveCouncilCouncilorRequest,
} from "dto/Council/ICouncilPortal";
import { ENotificationType, notification } from "utils/notification";

import CouncilorModal from "./CouncilorModal";
import PasswordModal from "./PasswordModal";
import { decodeText } from "./decodeText";
import styles from "./styles.module.scss";

// Biuro rady → „Radni". Odwzorowanie Kwantum.Cases.UI.CouncilSessionAgenda.Settings.CouncilorsList.
//
// To jedyne miejsce, w którym powstają konta portalu radnego: radny nie jest
// użytkownikiem Nota i nie ma jak sam się zarejestrować.

const roleLabel = (role: ECouncilorRole) => {
  if (role === ECouncilorRole.President) return "Przewodniczący rady";
  if (role === ECouncilorRole.Deputy) return "Zastępca przewodniczącego";
  return "Radny";
};

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pl-PL") : "—";

const CouncilorsView = () => {
  const [editing, setEditing] = useState<{ councilor: ICouncilCouncilor | null } | null>(
    null,
  );
  const [passwordFor, setPasswordFor] = useState<ICouncilCouncilor | null>(null);
  const [removing, setRemoving] = useState<ICouncilCouncilor | null>(null);

  const councilorsQuery = councilPortalService.useGetCouncilors();
  const settingsQuery = councilPortalService.useGetPortalSettings();
  const { mutate: save, isLoading: saving } = councilPortalService.useSaveCouncilor();
  const { mutate: setPassword, isLoading: settingPassword } =
    councilPortalService.useSetCouncilorPassword();
  const { mutate: remove } = councilPortalService.useRemoveCouncilor();

  const councilors = useMemo(
    () =>
      (councilorsQuery.data?.data ?? []).map((c) => ({
        ...c,
        FirstName: decodeText(c.FirstName),
        LastName: decodeText(c.LastName),
        Email: decodeText(c.Email),
        Login: decodeText(c.Login),
      })),
    [councilorsQuery.data],
  );

  const settings = settingsQuery.data?.data;
  const activeCount = councilors.filter((c) => c.IsActive).length;

  const doSave = (request: ISaveCouncilCouncilorRequest) => {
    save(request, {
      onSuccess: () => {
        setEditing(null);
        councilorsQuery.refetch();
        settingsQuery.refetch();
      },
      onError: (e) =>
        notification({
          type: ENotificationType.ERROR,
          title: "Błąd",
          text: errorText(e, "Nie udało się zapisać radnego."),
        }),
    });
  };

  const doSetPassword = (password: string) => {
    if (!passwordFor) return;

    setPassword(
      { id: passwordFor.Id, password },
      {
        onSuccess: () => {
          setPasswordFor(null);
          councilorsQuery.refetch();
          notification({
            type: ENotificationType.SUCCESS,
            title: "Hasło zmienione",
            text: "Radny został wylogowany ze wszystkich urządzeń.",
          });
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się zmienić hasła."),
          }),
      },
    );
  };

  const doRemove = () => {
    if (!removing) return;
    const id = removing.Id;
    setRemoving(null);

    remove(id, {
      onSuccess: () => {
        councilorsQuery.refetch();
        settingsQuery.refetch();
      },
      onError: (e) =>
        notification({
          type: ENotificationType.ERROR,
          title: "Błąd",
          text: errorText(e, "Nie udało się usunąć radnego."),
        }),
    });
  };

  if (councilorsQuery.isLoading) {
    return <Loading />;
  }

  const statutory = settings?.TotalCouncilorsCount ?? null;
  const mismatch = statutory !== null && statutory !== activeCount;

  return (
    <div className={styles.panel}>
      <div className={styles.topBar}>
        <span className={styles.listHeading}>
          Radni ({activeCount}
          {statutory !== null ? ` z ${statutory}` : ""})
        </span>

        {mismatch && (
          <span className={styles.warningBadge}>
            Liczba aktywnych kont nie zgadza się z ustawowym składem rady — wyniki
            głosowań w trybach liczonych od składu będą błędne.
          </span>
        )}

        <div className={styles.spacer} />

        <Button
          text="Nowy radny"
          element={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => setEditing({ councilor: null })}
        />
      </div>

      <div className={styles.listWrap}>
        {councilors.map((c) => (
          <div key={c.Id} className={styles.councilorRow}>
            <div className={styles.councilorMain}>
              <span className={styles.councilorName}>
                {c.LastName} {c.FirstName}
                {!c.IsActive && <span className={styles.inactiveTag}>nieaktywny</span>}
              </span>
              <span className={styles.councilorMeta}>
                {roleLabel(c.Role)} · login: {c.Login}
                {c.Email ? ` · ${c.Email}` : ""}
              </span>
            </div>

            <div className={styles.councilorStatus}>
              {c.HasPassword ? (
                <span className={styles.councilorMeta}>
                  ostatnia aktywność: {formatDateTime(c.LastActivity)}
                </span>
              ) : (
                <span className={styles.warningInline}>brak hasła — nie zaloguje się</span>
              )}
            </div>

            <div className={styles.rowTools}>
              <button
                type="button"
                className={styles.toolBtn}
                title="Ustaw hasło"
                onClick={() => setPasswordFor(c)}
              >
                <FontAwesomeIcon icon={faKey} />
              </button>
              <button
                type="button"
                className={styles.toolBtn}
                title="Edytuj"
                onClick={() => setEditing({ councilor: c })}
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
              <button
                type="button"
                className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                title="Usuń"
                onClick={() => setRemoving(c)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        ))}

        {!councilors.length && (
          <div className={styles.emptyAgenda}>
            Nie dodano jeszcze żadnego radnego. Bez kont radnych portal działa wyłącznie
            w trybie publicznym.
          </div>
        )}
      </div>

      {editing && (
        <CouncilorModal
          councilor={editing.councilor}
          cameras={settingsQuery.data?.data?.Cameras ?? []}
          loading={saving}
          onClose={() => setEditing(null)}
          onApply={doSave}
        />
      )}

      {passwordFor && (
        <PasswordModal
          title={`Hasło — ${passwordFor.LastName} ${passwordFor.FirstName}`}
          loading={settingPassword}
          onClose={() => setPasswordFor(null)}
          onApply={doSetPassword}
        />
      )}

      {removing && (
        <ConfirmModal
          title="Usunąć radnego?"
          text={`Konto „${removing.LastName} ${removing.FirstName}” straci dostęp do portalu. Oddane wcześniej głosy pozostaną w metrykach głosowań.`}
          acceptLabel="Usuń"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={doRemove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
};

export default CouncilorsView;
